import {
	Bloom,
	BrightnessContrast,
	ChromaticAberration,
	DepthOfField,
	EffectComposer,
	HueSaturation,
	N8AO,
	Vignette,
} from "@react-three/postprocessing";
import { useEffect, useMemo } from "react";
import * as THREE from "three";
import { FilmGrainEffect } from "./filmGrainEffect";
import { resolvePostProcessingBudget } from "./postProcessingBudget";
import type { ViewerRuntimeProfile } from "./viewerConfig";
import { AGX_SATURATION_COMPENSATION } from "./viewerConfig";

interface ScenePostProcessingProps {
	runtimeProfile: ViewerRuntimeProfile;
	/** Finished hot-room span; absent keeps exterior occlusion unchanged. */
	interiorRoomSpanM?: number;
	/**
	 * The section view's stencil caps write to the stencil buffer during
	 * the scene render. The composer renders the scene into its own
	 * buffer, which only carries a stencil attachment when this is true —
	 * so caps only show through the composer with it enabled. Off in every
	 * other mode to avoid paying for an attachment nothing reads.
	 */
	needsStencil?: boolean;
}

/**
 * Post-processing pipeline: N8AO contact shadows + subtle bloom.
 *
 * N8AO replaces the previous SSAO implementation for two reasons:
 * 1. Better compatibility with Three.js r182+ (no NormalPass dependency)
 * 2. Higher visual quality at lower sample counts — designed for archviz
 *
 * Bloom is intentionally restrained: only warm highlights and interior
 * glow should pick up visible bloom. If the user can "see" the bloom
 * effect, it's too strong.
 */
export function ScenePostProcessing({
	runtimeProfile,
	interiorRoomSpanM,
	needsStencil = false,
}: ScenePostProcessingProps) {
	// Keep occlusion local to joints and bench supports. A metre-wide radius
	// stained whole panels and overwhelmed the wood's own tonal variation.
	const {
		multisampling,
		aoRadius,
		aoSamples,
		halfRes,
		hasAO,
		hasBloom,
		hasGrade,
		hasDof,
		grainStrength,
		hasChromaticAberration,
	} = resolvePostProcessingBudget(runtimeProfile, interiorRoomSpanM);
	// Strength is baked at construction; it only changes with the tier,
	// which remounts the composer's effect list anyway.
	const grainEffect = useMemo(
		() =>
			grainStrength > 0
				? new FilmGrainEffect({ strength: grainStrength })
				: null,
		[grainStrength],
	);
	useEffect(() => () => grainEffect?.dispose(), [grainEffect]);
	// Offset in UV units at the frame edge; `radialModulation` keeps the
	// centre (where the sauna sits) perfectly sharp.
	const aberrationOffset = useMemo(() => new THREE.Vector2(0.0011, 0.0007), []);

	if (!hasAO && !hasBloom && !hasGrade && !hasDof) return null;

	const effects = [];
	if (hasAO) {
		effects.push(
			<N8AO
				key="ao"
				aoRadius={aoRadius}
				intensity={1.1 + runtimeProfile.contactShadowStrength * 0.85}
				distanceFalloff={0.6}
				aoSamples={aoSamples}
				halfRes={halfRes}
				screenSpaceRadius={false}
				color={0x1a1408}
			/>,
		);
	}

	if (hasBloom) {
		effects.push(
			<Bloom
				key="bloom"
				luminanceThreshold={runtimeProfile.bloomThreshold}
				luminanceSmoothing={0.7}
				intensity={runtimeProfile.bloomIntensity}
				mipmapBlur
			/>,
		);
	}

	if (hasGrade) {
		effects.push(
			<HueSaturation
				key="grade-hue"
				hue={runtimeProfile.gradeWarmth * 0.06}
				saturation={
					AGX_SATURATION_COMPENSATION + runtimeProfile.gradeWarmth * 0.1
				}
			/>,
			<BrightnessContrast
				key="grade-contrast"
				contrast={runtimeProfile.gradeContrast}
				brightness={runtimeProfile.gradeWarmth * 0.012}
			/>,
			<Vignette
				key="grade-vignette"
				eskil={false}
				offset={0.18}
				darkness={0.3 + runtimeProfile.gradeContrast * 0.35}
			/>,
		);
	}

	if (hasChromaticAberration) {
		effects.push(
			<ChromaticAberration
				key="lens-aberration"
				offset={aberrationOffset}
				radialModulation
				modulationOffset={0.42}
			/>,
		);
	}

	if (hasDof) {
		// Subtle architectural-photo bokeh: focusDistance puts the
		// model centre in focus (camera at far=100 makes 0.025 ≈ 2.5 m)
		// and bokehScale stays low so background foliage / hedge gets a
		// soft falloff without smearing the sauna itself.
		effects.push(
			<DepthOfField
				key="dof"
				focusDistance={0.025}
				focalLength={0.05}
				bokehScale={2}
			/>,
		);
	}

	// Grain goes last, after DoF, so the blur cannot smear it: real grain
	// sits on the film, not in the scene.
	if (grainEffect) {
		effects.push(<primitive key="film-grain" object={grainEffect} />);
	}

	return (
		<EffectComposer
			stencilBuffer={needsStencil}
			multisampling={multisampling}
		>
			{effects}
		</EffectComposer>
	);
}
